/**
 * /api/v1/Crew — raw axios calls returning backend DTO shapes verbatim.
 * Frontend mappers (Phase 7b+) translate to the existing UI types.
 *
 * Auth: token attached by api_helper.ts axios interceptor. Tenant filter
 * + privilege gate enforced server-side.
 */

import axios from 'axios';
import config from '../../../../../config';
import { unwrap } from '../http';
import type {
  BackendCrew,
  BackendCrewMember,
  BackendCrewAsset,
  CreateUpdateResponse,
  DeleteResponse,
  TupleResponse,
  CrewCreateRequest,
  CrewUpdateRequest,
  CrewMemberCreateRequest,
  CrewAssetCreateRequest,
  CrewSetLeadRequest,
} from '../types';

// Match the project's existing service pattern (assetService / leadService):
// use absolute URLs via config.api.API_URL. baseURL is also set but services
// don't rely on it — keeps URL construction explicit per service.
const BASE = `${config.api.API_URL}/Crew`;

export const crewService = {
  /* ── Crew CRUD ─────────────────────────────────────────────────────── */
  getSingle: (id: string) =>
    axios.get<BackendCrew>(`${BASE}/${id}`).then(unwrap),

  getAll: (pageNumber = 1, pageSize = 50) =>
    axios
      .get<TupleResponse<BackendCrew>>(BASE, { params: { pageNumber, pageSize } })
      .then(unwrap),

  /** All crews on a calendar date — members + assets pre-loaded; no activities. */
  getByDate: (date: string) =>
    axios.get<BackendCrew[]>(`${BASE}/by-date`, { params: { date } }).then(unwrap),

  create: (body: CrewCreateRequest) =>
    axios.post<CreateUpdateResponse<BackendCrew>>(BASE, body).then(unwrap),

  update: (id: string, body: CrewUpdateRequest) =>
    axios.put<CreateUpdateResponse<BackendCrew>>(`${BASE}/${id}`, body).then(unwrap),

  delete: (id: string) =>
    axios.delete<DeleteResponse>(`${BASE}/${id}`).then(unwrap),

  restore: (id: string) =>
    axios.post<CreateUpdateResponse<BackendCrew>>(`${BASE}/${id}/restore`).then(unwrap),

  /* ── Members ──────────────────────────────────────────────────────── */
  getMembers: (crewId: string) =>
    axios.get<BackendCrewMember[]>(`${BASE}/${crewId}/members`).then(unwrap),

  addMember: (crewId: string, body: CrewMemberCreateRequest) =>
    axios
      .post<CreateUpdateResponse<BackendCrewMember>>(`${BASE}/${crewId}/members`, body)
      .then(unwrap),

  removeMember: (memberId: string) =>
    axios.delete<DeleteResponse>(`${BASE}/members/${memberId}`).then(unwrap),

  /* ── Assets ───────────────────────────────────────────────────────── */
  getAssets: (crewId: string) =>
    axios.get<BackendCrewAsset[]>(`${BASE}/${crewId}/assets`).then(unwrap),

  addAsset: (crewId: string, body: CrewAssetCreateRequest) =>
    axios
      .post<CreateUpdateResponse<BackendCrewAsset>>(`${BASE}/${crewId}/assets`, body)
      .then(unwrap),

  removeAsset: (assetId: string) =>
    axios.delete<DeleteResponse>(`${BASE}/assets/${assetId}`).then(unwrap),

  /* ── Lead atomic demote/promote ───────────────────────────────────── */
  setLead: (body: CrewSetLeadRequest) =>
    axios.put<CreateUpdateResponse<BackendCrew>>(`${BASE}/set-lead`, body).then(unwrap),
};
